import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import CoreSplitter from '@components-clean/core/core-splitter';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { GridViewMenuItem } from '@components-clean/core/grid/grid-menu/grid-view-menu';
import Grid from '@components-clean/core/grid-fe';
import { DateTimeCell, YesNoCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import { PanelType } from 'types/graphql-global-types';
import { useBoolean } from '@hooks/index';
import { GridFullView, GridViewOptions, SplitterOrientation } from '@utils/constants';
import { GET_PANELS } from '@queries/central-monitoring';
import {
    getPanels_OpraQueries_panels_Panel,
    getPanels,
    getPanelsVariables,
} from '@queries/generated/central-monitoring.generated';

const FeGridPage = () => {
    const { data: panelsData } = useQuery<getPanels, getPanelsVariables>(GET_PANELS, {
        variables: {
            organisationId: '640cb636e37e8f000139518e',
            studyId: '640cb705e37e8f0001395191',
        },
    });

    const { t } = useTranslation();

    const [currentView, setCurrentView] = useState<GridViewOptions>(GridFullView.full);
    const showAdd = useBoolean(false);

    const isFullView = currentView === GridFullView.full;

    const gridViewOptions: GridViewMenuItem[] = [
        {
            label: t('ra:fullView'),
            onClick: () => setCurrentView(GridFullView.full),
            icon: (
                <FontAwesomeIcon
                    icon={['fas', 'square']}
                    fontSize={18}
                    className="k-ml-1px k-mt--1px"
                    style={{ transform: 'scale(1.15,1)' }}
                />
            ),
            isActive: currentView === GridFullView.full,
        },
        {
            label: t('ra:topView'),
            onClick: () => setCurrentView(SplitterOrientation.vertical),
            icon: <FontAwesomeIcon icon={['far', 'window-maximize']} fontSize={18} className="k-mt--1px" />,
            isActive: currentView === SplitterOrientation.vertical,
        },
    ];

    const formattedData = (): (getPanels_OpraQueries_panels_Panel | null)[] | undefined =>
        panelsData?.panels?.flatMap((panel) => {
            return panel ?? null;
        });
    const [data, setData] = useState<unknown[] | undefined>();
    useEffect(() => {
        setData(undefined);
    }, [panelsData]);

    const panelsColumns: GridColumnProps[] = [
        {
            title: 'Active',
            field: 'active',
            filter: 'boolean',
            cells: { data: YesNoCell },
        },
        {
            title: 'Name',
            field: 'name',
            filter: 'text',
            showTooltip: false,
        },
        {
            title: 'Panel Type',
            field: 'panelType',
            filter: 'text',
        },
        {
            title: 'Created',
            field: 'created',
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy HH:mm:ss') },
        },
        {
            title: 'RecordID',
            field: 'recordId',
            filter: 'numeric',
        },
    ];

    const headers = [
        { id: 1, label: 'Panels' },
        { id: 2, label: 'Panels (Models)' },
        { id: 3, label: 'Panels (Graph)' },
    ];

    const newPanGrid = (isfvparam: boolean) => (
        <Grid
            title={headers}
            onTitleChange={(e) => {
                if (!e.value) return;
                const value = e.value as (typeof headers)[0];
                if (value.id === 1) setData(formattedData());
                else if (value.id === 2) setData(formattedData()?.filter((x) => x?.panelType === PanelType.MODEL));
                else setData(formattedData()?.filter((x) => x?.panelType === PanelType.GRAPH));
            }}
            canEdit
            data={data}
            maxHeight={isfvparam ? 'calc(100vh - 136px)' : 'auto'}
            testDataId="grid-panels-test"
            columns={panelsColumns}
            gridViewOptions={gridViewOptions}
            sortable={{ mode: 'single', allowUnsort: true }}
            exportFilename={`PanelData${moment(new Date()).format('yyyy-MM-DD')}`}
            addCallback={showAdd.setTrue}
            selectable={{ enabled: true, mode: 'single' }}
            dataItemKey="id"
            showPlaceholder={false}
            onSelectionChange={(event, selectedState) => {
                // When selection state is rows then selectedState is a dictionary of boolean's,
                // keyed on the value from the dataItemKey field for that row
                // When select state is cells, then selectedState is a dictionary of array numbers
                // where the number is the index of the cell, keyed on the value from the dataItemKey field for that row
                // We can use the dictionary key to get the data item(s), alternatively, because this grid
                // is implementing single select, we could just use the `event.startDataItem` value
                window.console.log({ event, selectedState });
            }}
            rowMenuActions={[
                {
                    id: 1,
                    label: t('edit'),
                    onClick: () => {},
                    icon: ['far', 'pen-to-square'],
                },

                {
                    id: 2,
                    label: t('add'),
                    onClick: () => {},
                    icon: ['far', 'add'],
                },
                {
                    id: 3,
                    label: t('delete'),
                    onClick: () => {},
                    icon: ['far', 'trash'],
                },
            ]}
            rowReorder
            saveReorder={(newData) => {
                setData(Array.isArray(newData) ? newData : newData?.data);
                return true;
            }}
            reorderConfirmation={(onSubmit, onClose) => {
                return function dialog() {
                    return (
                        <DraggableDialog
                            dataTestId="reorder-data-warning"
                            onSubmit={() => {
                                onSubmit();
                            }}
                            onClose={() => {
                                onClose();
                            }}
                        >
                            Custom warning message can be displayed here
                        </DraggableDialog>
                    );
                };
            }}
        />
    );

    return (
        <>
            {showAdd.value && (
                <DraggableDialog
                    dataTestId="dummy-add-dialog"
                    title="Add Clicked"
                    onClose={showAdd.setFalse}
                    onSubmit={showAdd.setFalse}
                >
                    This will be a form or some other way to add data to this grid!
                </DraggableDialog>
            )}
            {isFullView ? (
                newPanGrid(isFullView)
            ) : (
                <div style={{ height: 'calc(100vh - 132px)' }}>
                    <CoreSplitter
                        className="!k-h-full"
                        orientation={currentView}
                        alignmentValues={[
                            {
                                size: '50%',
                                min: '30%',
                            },
                            {},
                        ]}
                        data-testid="test-splitter"
                    >
                        <div className="k-h-full">{newPanGrid(isFullView)}</div>
                        <div>
                            Duis fermentum rutrum fringilla. Donec quis orci sit amet ex elementum lacinia. Suspendisse
                            bibendum lectus tincidunt efficitur mattis. Praesent tincidunt sapien ante. Mauris quis quam
                            arcu. Curabitur eu cursus nisl, sit amet pharetra massa. In imperdiet diam id purus maximus
                            interdum id in nisl. Curabitur porta neque ut augue pretium bibendum. Orci varius natoque
                            penatibus et magnis dis parturient montes, nascetur ridiculus mus. Phasellus ut condimentum
                            leo. Nulla in ipsum id lacus lobortis ultricies. Sed consectetur leo in lectus finibus
                            laoreet vitae nec massa.
                        </div>
                    </CoreSplitter>
                </div>
            )}
        </>
    );
};

export default FeGridPage;
